import { sokumRnBul } from "@oasis/domain";
import { cevir } from "@/lib/dil";
import { AG_HATASI, dosyaTuru, sirali, type DosyaBenzeri, type Ton } from "@/lib/mrb-toplu";

/**
 * D-061: "Upload Disassembly reports" penceresinin saf mantığı (MRB penceresindeki `mrb-toplu.ts`'in eşi). Ekran
 * (SokumTopluYukleme.tsx) yalnız çizer; eşleme, satır durumları, sunucu yanıtının satır sonucuna çevrilmesi, sıradaki satırın
 * seçimi ve gösterilecek metinler burada. JSX yok; React ve oturum katmanını yüklemez.
 * MRB penceresinden bilinçli farklar: eşleme anahtarı riser numarasıdır (`sokumRnBul`); tek başına excel de GÖNDERİLİR, çünkü
 * söküm girdisi olmayan kayda bağlanıp bağlanmayacağına ("disassembly input attached" · "needs its PDF") sunucu karar verir.
 */

/**
 * bekliyor: gönderilecek · yukleniyor: istek sürüyor · tamam: sunucu yanıtladı (`sonuc` dolu) ·
 * rn_gerekli: riser okunamadı, satırda seçilir · hata: sunucu ya da ağ hatası.
 */
export type SatirDurumu = "bekliyor" | "yukleniyor" | "tamam" | "rn_gerekli" | "hata";

/** Söküm girdisi okunurken MRB eşleşmesinin sonucu (sunucudaki `GirdiMrbSonucu`). */
export interface MrbSonucu {
  durum: "eslesti" | "mrb_yok" | "sn_yok" | "bagsiz";
  sn: string | null;
}

/** `POST /api/sokum/toplu` yanıtı (sözleşme U-053). */
export interface TopluYaniti {
  sonuc: "yuklendi" | "girdi_baglandi" | "degismedi" | "pdf_gerekli";
  rn: string;
  /** Söküm kaydı; yalnız "pdf_gerekli"de null. */
  id: string | null;
  ozet?: { satir: number; birim: number; celiski: number };
  tamamlandi?: boolean;
  /** girdi_baglandi: kayıt söküm girdisini bekliyordu ve bu excel'le tamamlandı. */
  bekliyordu?: boolean;
  kuyrugaGirdi?: boolean;
  siraNo?: number | null;
  /** girdi_baglandi: kaydın dönüşüm işi düştü (sıradaydı, dönüştürülüyordu ya da hata vermişti). */
  siradanCikti?: boolean;
  /** girdi_baglandi: satırda PDF de vardı; kayıttaki PDF kaldı, satırdaki kaydedilmedi. */
  pdfKaydedilmedi?: boolean;
  mrb?: MrbSonucu;
  teslimAtlandi?: boolean;
  uyarilar: string[];
}

export interface SatirHatasi {
  /** Sunucunun İngilizce metni (gösterimde çevrilir) ya da bu modülün metni. */
  metin: string;
  kaynak: "rn" | "excel" | null;
  /** Ağ hatası ya da sunucu arızası (5xx): aynı satır olduğu gibi yeniden denenebilir. */
  tekrarlanabilir: boolean;
}

export interface SokumSatiri<D extends DosyaBenzeri = File> {
  id: number;
  pdf: D | null;
  excel: D | null;
  /** Dosya adından okunan riser (eşleme bununla yapılır). */
  rn: string | null;
  /** Kullanıcının satırda seçtiği riser; sunucuya yalnız bu gider (dosya adındakini sunucu kendisi okur). */
  secilenRn: string | null;
  durum: SatirDurumu;
  sonuc: TopluYaniti | null;
  hata: SatirHatasi | null;
}

/** Satırın riseri: satırda seçilen, yoksa dosya adındaki. */
export const etkinRn = (s: Pick<SokumSatiri<DosyaBenzeri>, "rn" | "secilenRn">): string | null => s.secilenRn ?? s.rn;

function yeniSatir<D extends DosyaBenzeri>(id: number, pdf: D | null, excel: D | null, rn: string | null): SokumSatiri<D> {
  return { id, pdf, excel, rn, secilenRn: null, durum: rn ? "bekliyor" : "rn_gerekli", sonuc: null, hata: null };
}

/** PDF'ini bekleyen tek başına excel satırı: henüz gönderilmemiş ya da sunucudan "needs its PDF" almış. */
const pdfBekleyenExcel = (s: SokumSatiri<DosyaBenzeri>) => !s.pdf && Boolean(s.excel) && (s.durum === "bekliyor" || (s.durum === "tamam" && s.sonuc?.sonuc === "pdf_gerekli"));

/**
 * Seçilen ya da sürüklenen dosyaları satırlara ekler. Önce PDF'ler, sonra excel'ler işlenir ki aynı seçimde sıra fark etmesin.
 * - PDF: aynı riserde PDF'ini bekleyen tek başına excel satırı varsa ona girer ("needs its PDF" almış satır yeniden bekler);
 *   yoksa yeni satır. Aynı riserin ikinci PDF'i (".pdf" ve ".PDF") ayrı satır olur ve sunucudan "not changed" alır.
 * - Excel: aynı riserde excel'i olmayan, henüz gönderilmemiş PDF satırına girer; yoksa yeni satır. Tek başına excel de gönderilir.
 * Riser okunamazsa satır "rn_gerekli" olur. PDF ve .xlsx dışındaki dosyalar eklenmez, adları `atlanan`da döner.
 */
export function dosyalariEkle<D extends DosyaBenzeri>(satirlar: readonly SokumSatiri<D>[], dosyalar: readonly D[]): { satirlar: SokumSatiri<D>[]; atlanan: string[] } {
  const sonuc = satirlar.map((s) => ({ ...s }));
  let id = sonuc.reduce((m, s) => Math.max(m, s.id), 0);
  const atlanan = dosyalar.filter((f) => !dosyaTuru(f.name)).map((f) => f.name);

  for (const f of dosyalar.filter((x) => dosyaTuru(x.name) === "pdf")) {
    const rn = sokumRnBul(f.name);
    const bekleyen = rn ? sonuc.find((s) => pdfBekleyenExcel(s) && etkinRn(s) === rn) : undefined;
    if (bekleyen) {
      bekleyen.pdf = f;
      bekleyen.durum = "bekliyor";
      bekleyen.sonuc = null;
      continue;
    }
    sonuc.push(yeniSatir(++id, f, null, rn));
  }

  for (const f of dosyalar.filter((x) => dosyaTuru(x.name) === "excel")) {
    const rn = sokumRnBul(f.name);
    const pdfSatiri = rn ? sonuc.find((s) => s.durum === "bekliyor" && s.pdf && !s.excel && etkinRn(s) === rn) : undefined;
    if (pdfSatiri) {
      pdfSatiri.excel = f;
      continue;
    }
    sonuc.push(yeniSatir(++id, null, f, rn));
  }
  return { satirlar: sonuc, atlanan };
}

/**
 * Riseri okunamayan satırda seçilen riser ("Retry"): satır yeniden gönderilmek üzere bekler. Aynı riserin öteki yarısı ayrı
 * satırda gönderilmeyi bekliyorsa bu satıra taşınır, o satır kalkar: satırın excel'i yoksa PDF'ini bekleyen tek başına excel,
 * satırın PDF'i yoksa excel'i olmayan bekleyen PDF. Böylece ikisi tek istekte gider.
 */
export function rnSec<D extends DosyaBenzeri>(satirlar: readonly SokumSatiri<D>[], id: number, rn: string): SokumSatiri<D>[] {
  const hedef = satirlar.find((s) => s.id === id);
  if (!hedef || !rn || (!hedef.pdf && !hedef.excel)) return [...satirlar];
  const oteki = satirlar.find((s) => s.id !== id && etkinRn(s) === rn && (
    (hedef.pdf && !hedef.excel && pdfBekleyenExcel(s))
    || (!hedef.pdf && hedef.excel && s.durum === "bekliyor" && Boolean(s.pdf) && !s.excel)
  ));
  return satirlar
    .filter((s) => s !== oteki)
    .map((s) => (s.id === id ? { ...s, secilenRn: rn, pdf: s.pdf ?? oteki?.pdf ?? null, excel: s.excel ?? oteki?.excel ?? null, durum: "bekliyor" as const, sonuc: null, hata: null } : s));
}

/** Satırı olduğu gibi yeniden kuyruğa koyar (ağ hatası ya da sunucu arızasından sonra "Retry"). */
export function tekrarDene<D extends DosyaBenzeri>(satirlar: readonly SokumSatiri<D>[], id: number): SokumSatiri<D>[] {
  return satirlar.map((s) => (s.id === id && (s.pdf || s.excel) ? { ...s, durum: "bekliyor" as const, sonuc: null, hata: null } : s));
}

/** Excel'i reddedilen satır: excel satırdan çıkar, PDF tek başına yeniden gönderilir ("Retry without excel"). */
export function excelsizDene<D extends DosyaBenzeri>(satirlar: readonly SokumSatiri<D>[], id: number): SokumSatiri<D>[] {
  return satirlar.map((s) => (s.id === id && s.pdf ? { ...s, excel: null, durum: "bekliyor" as const, sonuc: null, hata: null } : s));
}

/**
 * Gönderilecek sıradaki satır: listede en üstteki bekleyen (riseri belli, en az bir dosyası olan). `secim` null ise ("Upload" turu)
 * bütün bekleyenler; küme ise yalnız içindeki satırlar (satır düğmesiyle yeniden denenenler). `secimeEkle` MRB penceresiyle ortaktır.
 */
export function siradaki<D extends DosyaBenzeri>(satirlar: readonly SokumSatiri<D>[], secim: ReadonlySet<number> | null = null): SokumSatiri<D> | null {
  return satirlar.find((s) => s.durum === "bekliyor" && (s.pdf || s.excel) && etkinRn(s) && (!secim || secim.has(s.id))) ?? null;
}

/**
 * Satırın isteği: `dosyaYukle("/sokum/toplu", dosya, alanlar, ekDosyalar, dosyaAlan)`. PDF varsa ana dosya odur ("dosya"), excel
 * yanında gider; tek başına excel "excel" alanında gider. Riser yalnız satırda seçildiyse gönderilir.
 */
export function istekParcalari<D extends DosyaBenzeri>(s: SokumSatiri<D>): { dosya: D; dosyaAlan: "dosya" | "excel"; alanlar: Record<string, string>; ekDosyalar: Record<string, D> } {
  const alanlar: Record<string, string> = s.secilenRn ? { rn: s.secilenRn } : {};
  if (s.pdf) return { dosya: s.pdf, dosyaAlan: "dosya", alanlar, ekDosyalar: s.excel ? { excel: s.excel } : {} };
  if (s.excel) return { dosya: s.excel, dosyaAlan: "excel", alanlar, ekDosyalar: {} };
  throw new Error("Dosya yok: bu satır gönderilmez.");
}

/** Gönderimin ham sonucu: 2xx gövdesi · 4xx/5xx (gövdesiyle) · ağ hatası. */
export type Gonderim =
  | { tur: "yanit"; govde: TopluYaniti }
  | { tur: "hata"; status: number; govde: unknown; mesaj: string }
  | { tur: "ag" };

type SatirSonucu = Pick<SokumSatiri, "durum" | "sonuc" | "hata">;

function hataSonucu(metin: string, kaynak: unknown, tekrarlanabilir: boolean): SatirSonucu {
  const k = kaynak === "rn" || kaynak === "excel" ? kaynak : null;
  return { durum: k === "rn" ? "rn_gerekli" : "hata", sonuc: null, hata: { metin, kaynak: k, tekrarlanabilir } };
}

/**
 * Sunucu yanıtını satır sonucuna çevirir. Hata 4xx gövdesinde `{hata, hataKaynagi}` ile gelir: "rn" → satırda riser seçimi;
 * "excel" → söküm girdisi reddedildi ("Retry without excel"); ağ hatası ve 5xx → "Retry".
 */
export function sonucuIsle(g: Gonderim): SatirSonucu {
  if (g.tur === "ag") return hataSonucu(AG_HATASI, null, true);
  if (g.tur === "hata") {
    const govde = (g.govde && typeof g.govde === "object" ? g.govde : {}) as { hata?: unknown; message?: unknown; hataKaynagi?: unknown };
    const metin = typeof govde.hata === "string" ? govde.hata : typeof govde.message === "string" ? govde.message : g.mesaj;
    return hataSonucu(metin, govde.hataKaynagi, g.status >= 500);
  }
  return { durum: "tamam", sonuc: g.govde, hata: null };
}

export interface SatirGorunumu {
  etiketler: { metin: string; ton: Ton }[];
  aciklama: { metin: string; ton: "ink-2" | "bad" } | null;
  /** Açıklamanın altındaki satırlar: MRB eşleşmesi (koyu; Assembly SN yoksa sarı), sunucunun uyarıları (sarı), teslim notu (gri). */
  notlar: { metin: string; ton: "ink-2" | "warn" | "ink-3" }[];
  /** RN hücresinde riser seçimi ve "Retry". */
  rnSecimi: boolean;
  dugme: "tekrar" | "excelsiz" | null;
}

/** Söküm girdisi okunan satırda MRB eşleşmesinin cümlesi; "bagsiz"da sebebi sunucunun uyarıları söyler. */
function mrbNotu(m: MrbSonucu | undefined): SatirGorunumu["notlar"] {
  if (!m) return [];
  if (m.durum === "eslesti") return [{ metin: cevir("MRB {0} matched: RN↔SN link established.", m.sn ?? "—"), ton: "ink-2" }];
  if (m.durum === "mrb_yok") return [{ metin: cevir("No MRB with SN {0} yet. It is linked automatically when that MRB is uploaded.", m.sn ?? "—"), ton: "ink-2" }];
  if (m.durum === "sn_yok") return [{ metin: cevir("The disassembly input gives no Assembly SN (blank or unreadable on the form), so no MRB will be linked automatically. If the form does show an SN, upload a corrected disassembly input on the record page."), ton: "warn" }];
  return [];
}

/** Satırın "Result" hücresi (tasarım: sokum--toplu-yukleme). */
export function satirGorunumu<D extends DosyaBenzeri>(satir: SokumSatiri<D>): SatirGorunumu {
  const bos: SatirGorunumu = { etiketler: [], aciklama: null, notlar: [], rnSecimi: false, dugme: null };
  switch (satir.durum) {
    case "bekliyor":
      return { ...bos, etiketler: [{ metin: cevir("waiting"), ton: "neutral" }] };
    case "yukleniyor":
      return { ...bos, etiketler: [{ metin: cevir("uploading…"), ton: "info" }] };
    case "rn_gerekli":
      return {
        ...bos,
        etiketler: [{ metin: cevir("riser could not be read"), ton: "warn" }],
        aciklama: { metin: satir.hata ? cevir(satir.hata.metin) : cevir("The file name has no riser number. Select the riser and retry this row."), ton: "ink-2" },
        rnSecimi: true,
      };
    case "hata": {
      const h = satir.hata;
      const etiketler: SatirGorunumu["etiketler"] = [{ metin: cevir("failed"), ton: "bad" }];
      if (!h) return { ...bos, etiketler, aciklama: { metin: cevir("Upload failed"), ton: "bad" } };
      // Reddedilen söküm girdisi PDF'le eşliyse PDF de kaydedilmemiştir: satır excel'siz yeniden denenebilir.
      if (h.kaynak === "excel" && satir.pdf) return { ...bos, etiketler, aciklama: { metin: cevir("Disassembly input rejected: {0} The PDF was not saved either.", cevir(h.metin)), ton: "bad" }, dugme: "excelsiz" };
      return { ...bos, etiketler, aciklama: { metin: cevir(h.metin), ton: "bad" }, dugme: h.tekrarlanabilir ? "tekrar" : null };
    }
    case "tamam": {
      const s = satir.sonuc;
      if (!s) return { ...bos, etiketler: [{ metin: cevir("uploaded"), ton: "ok" }] };
      const uyarilar: SatirGorunumu["notlar"] = s.uyarilar.map((u) => ({ metin: cevir(u), ton: "warn" }));
      const teslim: SatirGorunumu["notlar"] = s.teslimAtlandi ? [{ metin: cevir("{0} was still waiting on the Receiving list; Receiving is skipped.", s.rn), ton: "ink-3" }] : [];
      const o = s.ozet ?? { satir: 0, birim: 0, celiski: 0 };
      switch (s.sonuc) {
        case "degismedi":
          return { ...bos, etiketler: [{ metin: cevir("not changed"), ton: "warn" }], aciklama: { metin: cevir("{0} already has a Disassembly record; nothing from this row was saved.", s.rn), ton: "ink-2" } };
        case "pdf_gerekli":
          return { ...bos, etiketler: [{ metin: cevir("needs its PDF"), ton: "warn" }], aciklama: { metin: cevir("{0} has no Disassembly record, so an excel alone is not sent from here. Add its PDF to this window.", s.rn), ton: "ink-2" } };
        case "girdi_baglandi": {
          const cumle = s.bekliyordu
            ? cevir("The Disassembly record of {0} was waiting for its disassembly input, so this excel was read into it: {1} rows · {2} units · {3} conflicts. Record completed; {0} moved to the Field list.", s.rn, o.satir, o.birim, o.celiski)
            : s.pdfKaydedilmedi
              ? cevir("The Disassembly record of {0} had no disassembly input, so this excel was read into it: {1} rows · {2} units · {3} conflicts. The PDF on the record was kept; the PDF in this row was not saved.", s.rn, o.satir, o.birim, o.celiski)
              : cevir("The Disassembly record of {0} had no disassembly input, so this excel was read into it: {1} rows · {2} units · {3} conflicts. The PDF on the record was kept.", s.rn, o.satir, o.birim, o.celiski);
          const metin = s.siradanCikti ? `${cumle} ${cevir("The record left the converter queue.")}` : cumle;
          return { ...bos, etiketler: [{ metin: cevir("disassembly input attached"), ton: "ok" }], aciklama: { metin, ton: "ink-2" }, notlar: [...mrbNotu(s.mrb), ...uyarilar] };
        }
        case "yuklendi": {
          const metin = s.kuyrugaGirdi
            ? cevir("No disassembly input: added to the converter queue ({0}). {1} waits for the excel and is not on the Field list yet.", s.siraNo ? sirali(s.siraNo) : "—", s.rn)
            : cevir("Disassembly input read: {0} rows · {1} units · {2} conflicts. Record completed; {3} moved to the Field list. Not queued.", o.satir, o.birim, o.celiski, s.rn);
          return { ...bos, etiketler: [{ metin: cevir("uploaded"), ton: "ok" }], aciklama: { metin, ton: "ink-2" }, notlar: [...(s.kuyrugaGirdi ? [] : mrbNotu(s.mrb)), ...uyarilar, ...teslim] };
        }
      }
    }
  }
}

export interface TopluOzet {
  toplam: number;
  /** Sonucu belli satırlar (sırada ve yüklenmekte olanlar dışındakiler). */
  biten: number;
  yuklendi: number;
  girdiBaglandi: number;
  degismedi: number;
  rnGerekli: number;
  pdfGerekli: number;
  hata: number;
  yukleniyor: number;
  bekliyor: number;
}

/** Pencerenin ilerleme satırı: "13 of 16 done" ve durum sayaçları. */
export function topluOzet<D extends DosyaBenzeri>(satirlar: readonly SokumSatiri<D>[]): TopluOzet {
  const o: TopluOzet = { toplam: satirlar.length, biten: 0, yuklendi: 0, girdiBaglandi: 0, degismedi: 0, rnGerekli: 0, pdfGerekli: 0, hata: 0, yukleniyor: 0, bekliyor: 0 };
  for (const s of satirlar) {
    if (s.durum === "bekliyor") o.bekliyor++;
    else if (s.durum === "yukleniyor") o.yukleniyor++;
    else if (s.durum === "rn_gerekli") o.rnGerekli++;
    else if (s.durum === "hata") o.hata++;
    else if (s.sonuc?.sonuc === "girdi_baglandi") o.girdiBaglandi++;
    else if (s.sonuc?.sonuc === "degismedi") o.degismedi++;
    else if (s.sonuc?.sonuc === "pdf_gerekli") o.pdfGerekli++;
    else o.yuklendi++;
  }
  o.biten = o.toplam - o.bekliyor - o.yukleniyor;
  return o;
}

/**
 * Pencerenin RN seçicisindeki riserler: söküm kaydı olmayanlar ile söküm girdisi okunmamış eski rapor kaydı olanlar (yalnız bunlara
 * bir şey yüklenebilir), sıralı ve tekrarsız.
 */
export function secilebilirRnler(kayitsiz: readonly string[], sokumler: readonly { rn: string; tur: string; okundu: boolean }[]): string[] {
  return [...new Set([...kayitsiz, ...sokumler.filter((s) => s.tur === "eski_rapor" && !s.okundu).map((s) => s.rn)])].sort();
}
